import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from './config';

export const competenciesApi = {
    getCompetenciesByAbilityId: async (abilityId) => {
        const response = await axiosInstance.get('/competencies', {
            params: { ability_id: abilityId }
        });
        
        // Group competencies by category
        const competencies = response.data.data;
        const groupedCompetencies = competencies.reduce((acc, competency) => {
            const category = competency.category || 'uncategorized';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(competency);
            return acc;
        }, {
            skills: [],
            knowledge: [],
            mindset: [],
            tool: []
        });

        return groupedCompetencies;
    },

    getCompetencyById: async (competencyId) => {
        const response = await axiosInstance.get(`/competencies/${competencyId}`);
        return response.data.data;
    },

    getCompetenciesByIds: async (competencyIds) => {
        try {
            const response = await axiosInstance.post('/competencies/by-ids', {
                ids: competencyIds
            });
            return response.data.data;
        } catch (error) {
            console.error("Error fetching competencies:", error);
            throw error;
        }
    },
    
    getCompetenciesCountByCategory: async () => {
        const response = await axiosInstance.get('/competencies');
        const competencies = response.data.data;
        
        const categoryCounts = {};
        competencies.forEach(competency => {
            const category = competency.category;
            if (category) {
                if (!categoryCounts[category]) {
                    categoryCounts[category] = 0;
                }
                categoryCounts[category]++;
            }
        });

        return categoryCounts;
    },

    getCompetenciesCount: async () => {
        const response = await axiosInstance.get('/competencies/count');
        return response.data.data.count;
    },

    getAllCompetencies: async () => {
        const response = await axiosInstance.get('/competencies');
        return response.data.data;
    }
};

export const useCompetenciesByAbilityId = (abilityId) => {
    return useQuery({
        queryKey: ['competencies', 'ability', abilityId],
        queryFn: () => abilityId ? competenciesApi.getCompetenciesByAbilityId(abilityId) : Promise.resolve({
            skills: [],
            knowledge: [],
            mindset: [],
            tool: []
        }),
        enabled: !!abilityId,
    });
};

export const useCompetencyById = (competencyId) => {
    return useQuery({
        queryKey: ['competency', competencyId],
        queryFn: () => competencyId ? competenciesApi.getCompetencyById(competencyId) : Promise.resolve(null),
        enabled: !!competencyId,
    });
};

export const useCompetenciesByIds = (competencyIds) => {
    return useQuery({
        queryKey: ['competencies', 'ids', competencyIds],
        queryFn: () => competencyIds?.length > 0 
            ? competenciesApi.getCompetenciesByIds(competencyIds) 
            : Promise.resolve([]),
        enabled: !!competencyIds?.length,
    });
};

export const useCompetenciesCountByCategory = () => {
    return useQuery({
        queryKey: ['competencies', 'countByCategory'],
        queryFn: competenciesApi.getCompetenciesCountByCategory,
    });
};

export const useCompetenciesCount = () => {
    return useQuery({
        queryKey: ['competencies', 'count'],
        queryFn: competenciesApi.getCompetenciesCount,
    });
};

export const useAllCompetencies = () => {
    return useQuery({
        queryKey: ['competencies', 'all'],
        queryFn: competenciesApi.getAllCompetencies,
    });
};